import { ColumnDef } from "@tanstack/react-table";
import cn from "classnames";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { useRefetchQuery } from "libs/reactQuery";
import {
  ManagerOrderEntity,
  MANAGER_ORDERS_QUERY_KEY,
  useGetManagerOrders,
  useReassignOrderDriver
} from "modules/companyAdminAuth";
import { ORDER_STATUSES_LIST, OrderStatus } from "modules/orders";
import moment from "moment";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ItemsTable, Pagination, TableItemActionsControl } from "ui";

import { ReassignDriverModal } from "../../components";

import styles from "./CompanyOrders.module.scss";

type OrdersTab = "active" | "delivered" | "all";

const TABS: Array<{ value: OrdersTab; label: string }> = [
  { value: "active", label: "Active" },
  { value: "delivered", label: "Delivered" },
  { value: "all", label: "All" }
];

const isValidTab = (value: string): value is OrdersTab =>
  value === "active" || value === "delivered" || value === "all";

const TAB_STATUSES: Record<OrdersTab, OrderStatus[] | undefined> = {
  active: [
    OrderStatus.Placed,
    OrderStatus.Collecting,
    OrderStatus.PickedUpByCompany,
    OrderStatus.AssignedToDriver,
    OrderStatus.Active,
    OrderStatus.InDelivery
  ],
  delivered: [OrderStatus.Delivered, OrderStatus.Failed],
  all: undefined
};

const formatStatusLabel = (status: OrderStatus) => {
  const found = ORDER_STATUSES_LIST.find((option) => option.value === status);
  return {
    label: found?.label ?? status,
    backgroundColor: found?.backgroundColor ?? "#A8A8BB"
  };
};

const CompanyOrders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTab } = useParams();

  const tab: OrdersTab =
    activeTab && isValidTab(activeTab) ? activeTab : "active";

  const [page, setPage] = useState(1);
  const refetch = useRefetchQuery([MANAGER_ORDERS_QUERY_KEY]);

  const requestParams = useMemo(
    () => ({
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      skip: (page - 1) * GET_LIST_REQUEST_DEFAULT_LIMIT,
      statuses: TAB_STATUSES[tab]
    }),
    [page, tab]
  );

  const { orders, meta, isLoading } = useGetManagerOrders(requestParams);

  const [reassignTarget, setReassignTarget] =
    useState<ManagerOrderEntity | null>(null);
  const [openedActionsIndex, setOpenedActionsIndex] = useState<number | null>(
    null
  );

  const reassignMutation = useReassignOrderDriver({
    onSuccess: async () => {
      setReassignTarget(null);
      toast.success(t("Driver reassigned"));
      await refetch();
    }
  });

  const columns: Array<ColumnDef<ManagerOrderEntity, ManagerOrderEntity[]>> =
    useMemo(
      () => [
        {
          id: "number",
          header: t("Order #"),
          size: 16,
          minSize: 16,
          cell: ({ row }) => row.original.number ?? row.original.id.slice(0, 8)
        },
        {
          id: "customer",
          header: t("Customer"),
          size: 18,
          minSize: 18,
          cell: ({ row }) =>
            row.original.user
              ? `${row.original.user.firstname} ${row.original.user.lastname}`
              : "—"
        },
        {
          id: "driver",
          header: t("Driver"),
          size: 18,
          minSize: 18,
          cell: ({ row }) =>
            row.original.driver
              ? `${row.original.driver.firstname} ${row.original.driver.lastname}`
              : t("Unassigned")
        },
        {
          id: "total",
          header: t("Total"),
          size: 12,
          minSize: 12,
          cell: ({ row }) =>
            row.original.totalPrice
              ? row.original.totalPrice.toLocaleString()
              : "—"
        },
        {
          id: "status",
          header: t("Status"),
          size: 16,
          minSize: 16,
          cell: ({ row }) => {
            const statusMeta = formatStatusLabel(row.original.status);
            return (
              <span
                className={styles.orders__statusBadge}
                style={{ backgroundColor: statusMeta.backgroundColor }}
              >
                {t(statusMeta.label)}
              </span>
            );
          }
        },
        {
          id: "createdAt",
          header: t("Created"),
          size: 14,
          minSize: 14,
          cell: ({ row }) =>
            moment(row.original.createdAt).format("DD MMM YYYY HH:mm")
        },
        {
          id: "actions",
          header: () => null,
          size: 6,
          minSize: 6,
          cell: ({ row }) => (
            <TableItemActionsControl
              rowIndex={row.index}
              openedItemOptionsIndex={openedActionsIndex}
              setOpenedItemOptionsIndex={setOpenedActionsIndex}
              actionsList={[
                {
                  label: t("Reassign driver"),
                  onClick: () => setReassignTarget(row.original)
                }
              ]}
            />
          ),
          enableSorting: false
        }
      ],
      [openedActionsIndex, t]
    );

  const totalItems = meta?.itemCount ?? 0;

  return (
    <ContentLayout title={t("Orders")}>
      <BoxShadowLayout className={styles.orders}>
        <div className={styles.orders__header}>
          <div className={styles.orders__tabs}>
            {TABS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(styles.orders__tab, {
                  [styles["orders__tab--active"]]: tab === option.value
                })}
                onClick={() => {
                  setPage(1);
                  navigate(`/company/orders/${option.value}`);
                }}
              >
                {t(option.label)}
              </button>
            ))}
          </div>
        </div>

        <ItemsTable
          columns={columns}
          data={orders ?? []}
          isLoading={isLoading}
          skeletonHeight={56}
          sorting={[]}
          setSorting={() => undefined}
        />

        <Pagination
          currentPage={page}
          onChange={setPage}
          perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
          totalItems={totalItems}
        />
      </BoxShadowLayout>

      <ReassignDriverModal
        isOpen={!!reassignTarget}
        currentDriverId={reassignTarget?.driverId}
        isSubmitting={reassignMutation.isLoading}
        onClose={() => setReassignTarget(null)}
        onConfirm={(driverId) => {
          if (reassignTarget) {
            reassignMutation.mutate({
              orderId: reassignTarget.id,
              driverId
            });
          }
        }}
      />
    </ContentLayout>
  );
};

export default CompanyOrders;
